import { useEffect, useRef, useState } from 'react';

import type { OfferPreset } from '../types';
import { OfferStepperHeader } from './offer-stepper-header';
import { OfferHero } from './offer-hero';
import { OfferBenefits } from './offer-benefits';
import { OfferFreeGift } from './offer-free-gift';
import { OfferPayment } from './offer-payment';
import { OfferFeatureCards } from './offer-feature-cards';
import { OfferReviews } from './offer-reviews';
import { OfferStickyCta } from './offer-sticky-cta';

interface OfferSalesPageProps {
  preset: OfferPreset;
  /** Step labels for all configured offers (drives the progress bar). */
  offerLabels: string[];
  /** Index of this offer within the funnel. */
  currentIndex: number;
  reportWas?: string;
  reportNow: string;
  purchasing: boolean;
  /** Show the money-back + subscription-consent block (subscription offers only). */
  showDisclaimer?: boolean;
  /** Plan-aware consent copy for the disclaimer (built from the offer plan). */
  consentText?: string;
  onUnlock: () => void;
  onSkip: () => void;
}

/**
 * Presentational sales page for one additional offer: stepper header, then the
 * hero/benefits/gift + payment block, content cards, reviews, and a repeated
 * hero + payment at the bottom. Built entirely from a preset + resolved pricing.
 */
export function OfferSalesPage({
  preset,
  offerLabels,
  currentIndex,
  reportWas,
  reportNow,
  purchasing,
  showDisclaimer,
  consentText,
  onUnlock,
  onSkip,
}: OfferSalesPageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  // Hide the pinned CTA whenever a real inline Unlock button is on screen.
  const [stickyHidden, setStickyHidden] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const targets = root.querySelectorAll('[data-offer-cta]');
    if (!targets.length) return;

    const visible = new Set<Element>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        }
        setStickyHidden(visible.size > 0);
      },
      // Hide once an inline button reaches the pinned bar's visible button
      // (~80px tall). Tighter than the bar's full height so the LAST inline CTA
      // — which can't scroll any higher — still crosses the line at max scroll
      // and hides the sticky one, with no extra bottom spacing needed.
      { rootMargin: '0px 0px -80px 0px' }
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [currentIndex]);

  const payment = (
    <OfferPayment
      heading={preset.price.heading}
      reportLabel={preset.price.reportLabel}
      reportWas={reportWas}
      reportNow={reportNow}
      giftLabel={preset.price.giftLabel}
      giftStrike={preset.gift.strikePrice}
      ctaLabel={preset.ctaLabel}
      purchasing={purchasing}
      showDisclaimer={showDisclaimer}
      consentText={consentText}
      onUnlock={onUnlock}
    />
  );

  const hero = (
    <OfferHero
      title={preset.hero.title}
      subtitle={preset.hero.subtitle}
      bg={preset.hero.bg}
      sign={preset.hero.sign}
      fullBleed={preset.hero.fullBleed}
    />
  );

  return (
    // Full-width dark backdrop so the side gutters (outside the mobile column)
    // don't show the white body background on wider screens.
    <div className="min-h-[100dvh] w-full bg-dark-base">
      <div
        ref={rootRef}
        data-slot="offer-sales-page"
        className="mx-auto flex min-h-[100dvh] max-w-app flex-col bg-dark-base text-purple-light"
        style={{ fontFamily: '"Red Hat Text Variable", sans-serif' }}
      >
        <OfferStepperHeader
          offerLabels={offerLabels}
          currentIndex={currentIndex}
          unlockedLabel={preset.unlockedLabel}
          onSkip={onSkip}
        />

        <div className="flex flex-col gap-8 px-4 pb-10 pt-6">
          <div className="flex flex-col gap-4">
            {hero}
            <OfferBenefits items={preset.benefits} />
            <OfferFreeGift
              title={preset.gift.title}
              subtitle={preset.gift.subtitle}
              image={preset.gift.image}
              fillImage={preset.gift.fillImage}
            />
          </div>

          {payment}

          <OfferFeatureCards
            heading={preset.featureCards.heading}
            cards={preset.featureCards.cards}
          />

          <OfferReviews
            heading={preset.reviews.heading}
            items={preset.reviews.items}
          />

          <div className="flex flex-col gap-4">
            {hero}
            <OfferBenefits items={preset.benefits} />
          </div>

          {payment}
        </div>

        <OfferStickyCta
          label={preset.ctaStickyLabel ?? preset.ctaLabel}
          onUnlock={onUnlock}
          purchasing={purchasing}
          hidden={stickyHidden}
        />
      </div>
    </div>
  );
}
